import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type TooltipModel = {
  who: string;
  what: ReactNode;
  statusLabel: string;
  statusColor: string;
  body: ReactNode;
  hint?: string;
};

export type TooltipHandle = {
  show: (model: TooltipModel, x: number, y: number) => void;
  move: (x: number, y: number) => void;
  hide: () => void;
};

function place(el: HTMLDivElement, x: number, y: number) {
  const pad = 14;
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  let left = x + 16;
  let top = y + 16;
  if (left + w + pad > window.innerWidth) left = x - w - 16;
  if (top + h + pad > window.innerHeight) top = y - h - 16;
  el.style.left = `${Math.max(pad, left)}px`;
  el.style.top = `${Math.max(pad, top)}px`;
}

const HeatmapTooltip = forwardRef<TooltipHandle>((_props, ref) => {
  const elRef = useRef<HTMLDivElement>(null);
  const posRef = useRef({ x: 0, y: 0 });
  const [model, setModel] = useState<TooltipModel | null>(null);
  const modelRef = useRef<TooltipModel | null>(null);

  useImperativeHandle(ref, () => ({
    show(m, x, y) {
      posRef.current = { x, y };
      modelRef.current = m;
      setModel(m);
    },
    move(x, y) {
      posRef.current = { x, y };
      if (elRef.current && modelRef.current) place(elRef.current, x, y);
    },
    hide() {
      modelRef.current = null;
      setModel(null);
    },
  }));

  useLayoutEffect(() => {
    if (model && elRef.current)
      place(elRef.current, posRef.current.x, posRef.current.y);
  }, [model]);

  if (!model) return null;

  return (
    <div
      ref={elRef}
      className="fixed z-[80] pointer-events-none max-w-[300px] bg-white text-[#252525] border border-[#E6EAEE] rounded-[14px] px-[14px] py-[13px] text-[0.78rem] font-mulish shadow-[0_22px_50px_-18px_rgba(2,115,74,0.4)]"
    >
      <div className="font-extrabold text-[0.9rem] text-[#252525]">
        {model.who}
      </div>
      <div className="text-[#5b6167] mt-0.5 mb-2 leading-[1.35]">
        {model.what}
      </div>
      <div
        className="font-bold text-[0.72rem] uppercase tracking-wide mb-1"
        style={{ color: model.statusColor }}
      >
        {model.statusLabel}
      </div>
      {model.body}
      {model.hint && (
        <div className="mt-2 pt-2 border-t border-[#EEEEEE] text-[0.72rem] font-semibold et-analytics-placeholder text-[#B4B4B4]">
          {model.hint}
        </div>
      )}
    </div>
  );
});

HeatmapTooltip.displayName = "HeatmapTooltip";

export default HeatmapTooltip;
